/**
 * Consent-gated PostHog client — Radar product analytics ONLY.
 *
 * GA4 remains the acquisition/marketing baseline; PostHog exists for
 * /platform/app product telemetry. The SDK is dynamically imported and
 * initialized only when the centrally resolved effective permission allows
 * it — isAnalyticsAllowed() in consent-manager.ts, the same region-scoped
 * policy that governs GA4/Clarity in layout.tsx (explicit grant anywhere,
 * or the notice-opt-out regional default with no stored decision; a stored
 * denial always blocks). Otherwise nothing loads and nothing is sent.
 * Near-capture-only: autocapture, automatic pageviews, dead clicks, rage
 * clicks, heatmaps, session recording, surveys, and remote flags are all
 * disabled. The one automatic event kept on is $pageleave — required for
 * PostHog Installation Health and the source of native scroll-depth
 * properties. It is gated by the same SDK opt-in/opt-out state, so it fires
 * only when analytics are permitted and stops the moment consent is revoked.
 * $pageview is sent manually. Radar product events reach PostHog exclusively
 * through trackEvent() and the radar-events adapter.
 *
 * Identity: never call posthog.identify(). No session token, platform
 * token, email, or hash of those may be used as an identity —
 * person_profiles stays 'identified_only' so all events remain anonymous.
 */

import { isAnalyticsAllowed } from '@/lib/consent-manager'
import { resolveClientEnvironment } from '@/lib/analytics/environment'
import { isSitesControlPlanePath } from '@/lib/sites/control-plane'
import type { PostHog } from 'posthog-js'

type EventProperties = Record<string, string | number | boolean>

/**
 * The Radar Sites no-capture boundary, evaluated against the CURRENT location
 * rather than a React render's pathname.
 *
 * AnalyticsGate owns the lifecycle, but it cannot close a race it does not
 * observe: a consented visitor on marketing can start enablePostHogCapture(),
 * navigate into `/sites/invitations/<token>` while the posthog-js chunk is
 * still loading, and have the gate's disablePostHogCapture() hit a null client.
 * The pending enable would then resume, see consent still granted, opt in, and
 * let its caller capture a URL carrying the one-time invitation token. So every
 * init/enable/capture path here re-reads window.location.pathname at the moment
 * it acts — before init, after each awaited boundary, and immediately before
 * opt-in or capture — and before_send drops anything already queued in the SDK
 * (including the automatic $pageleave) while the boundary holds. This is the
 * last line, not the policy: consent and attribution are never touched here.
 */
function onSitesNoCaptureBoundary(): boolean {
  return (
    typeof window !== 'undefined' &&
    isSitesControlPlanePath(window.location.pathname)
  )
}

let client: PostHog | null = null
let initPromise: Promise<PostHog | null> | null = null
let lastPageviewUrl: string | null = null

// Final safety net (also wired as before_send): custom property keys that
// could ever carry PII, audited-site data, free-form text, or credentials
// are stripped before an event leaves the browser. PostHog's own
// `$`-prefixed internals (e.g. $current_url = OUR page URL) are exempt —
// the denylist targets custom payloads, where these names mean user data.
const DENYLISTED_PROPERTY_KEY =
  /email|domain|url|href|host|token|cookie|session|password|secret|api_?key|auth|message|question|prompt|answer|text|brand|content|exception|error_message/i

// PostHog stamps its own reserved, non-`$`-prefixed keys onto EVERY event's
// properties. `token` is required for ingestion — posthog-js hard-DROPS any
// event whose before_send hook removes it (it treats `token` as a required
// property and returns null from the beforeSend runner, killing the send
// before the network). `distinct_id` is the SDK's anonymous identity. Neither
// is user data, and the denylist's broad substring match would otherwise
// strip `token` (and silently disable ALL analytics). Exempt them: our own
// custom payloads are already sanitized at the capturePostHogEvent call site,
// so this final net must never corrupt PostHog's event envelope.
const POSTHOG_RESERVED_PROPERTY_KEY = new Set(['token', 'distinct_id'])

export function sanitizeEventProperties(
  props: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (
      !key.startsWith('$') &&
      !POSTHOG_RESERVED_PROPERTY_KEY.has(key) &&
      DENYLISTED_PROPERTY_KEY.test(key)
    ) {
      continue
    }
    out[key] = value
  }
  return out
}

function initOnce(): Promise<PostHog | null> {
  // Never bring the SDK up while standing on the boundary. Deliberately NOT
  // cached into initPromise: this is a location-scoped refusal, not the
  // permanent missing-key one, so normal init still happens on the way back out.
  if (onSitesNoCaptureBoundary()) return Promise.resolve(null)
  if (initPromise) return initPromise

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
  // US-cloud ingest host is a constant, so default it in code — it can never
  // be a missing build-time env var that silently disables PostHog. Only the
  // project key gates init now (one fewer NEXT_PUBLIC_* that must survive the
  // build). Set NEXT_PUBLIC_POSTHOG_HOST only to target a different region or
  // a self-hosted instance.
  const host =
    process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'
  if (!key) {
    // Missing key must fail safe: cache the null so every later capture call
    // is a silent no-op and the app is never affected.
    initPromise = Promise.resolve(null)
    return initPromise
  }

  initPromise = (async () => {
    const { default: posthog } = await import('posthog-js')
    const instance = posthog.init(key, {
      api_host: host,
      // Near-capture-only surface: everything automatic is off EXCEPT
      // $pageleave.
      autocapture: false,
      // $pageview is sent manually (capturePostHogPageview) for genuine SPA
      // navigation, so automatic pageview capture stays off.
      capture_pageview: false,
      // MUST be true, not the 'if_capture_pageview' default: with
      // capture_pageview:false that default suppresses $pageleave, which
      // leaves PostHog Installation Health flagging it missing and drops
      // native scroll-depth properties. true forces the native
      // pagehide/visibilitychange beacon; it stays gated by the SDK
      // opt-in/opt-out state (opt_out_capturing_by_default below), so it
      // only fires when analytics are permitted.
      capture_pageleave: true,
      capture_dead_clicks: false,
      rageclick: false,
      capture_heatmaps: false,
      disable_session_recording: true,
      disable_surveys: true,
      advanced_disable_flags: true,
      // Anonymous-only: identify() is never called, so no person profiles
      // are ever created and events stay anonymous.
      person_profiles: 'identified_only',
      // The SDK starts opted out even though init only runs post-consent;
      // enablePostHogCapture() opts in explicitly after re-checking consent.
      opt_out_capturing_by_default: true,
      // localStorage-only persistence: no PostHog cookie is ever written.
      persistence: 'localStorage',
      before_send: event => {
        if (!event) return null
        // Defense in depth for anything the SDK itself schedules — above all
        // the automatic $pageleave, and any event queued before a navigation
        // into the control plane. `$`-prefixed URL fields are exempt from the
        // property denylist, so the whole event has to go, not its properties.
        if (onSitesNoCaptureBoundary()) return null
        if (event.properties) {
          event.properties = sanitizeEventProperties(
            event.properties
          ) as typeof event.properties
        }
        return event
      },
    })
    client = instance ?? posthog
    // Deployment environment on EVERY event, registered once rather than
    // passed per call — a super property cannot be forgotten by a new call
    // site, which is the only way this stays true as surfaces are added.
    //
    // Before this, the PostHog project filter excluded localhost/127.0.0.1 and
    // nothing else, so preview deployments were indistinguishable from
    // production and no figure in the 2026-09-19 audit could be defended.
    // `env` is deliberately named to clear the before_send denylist; `host`
    // would match it and be stripped in silence.
    //
    // Advisory, not authoritative: this is a build-time constant describing
    // the deployment that served the page. Server rows carry their own
    // server-resolved value, and that is the one reporting trusts.
    try {
      client.register({ env: resolveClientEnvironment() })
    } catch {
      // Analytics must never break the product.
    }
    return client
  })().catch(() => {
    client = null
    return null
  })

  return initPromise
}

/**
 * Consent granted: initialize the SDK (once) and opt in to capture.
 * Safe to call repeatedly — repeated consent events cannot re-initialize
 * or duplicate anything.
 */
export async function enablePostHogCapture(): Promise<void> {
  if (typeof window === 'undefined') return
  if (onSitesNoCaptureBoundary()) return
  if (!isAnalyticsAllowed()) return
  const ph = await initOnce()
  if (!ph) return
  // Consent may have been revoked — or the visitor may have navigated onto the
  // Sites boundary — while the SDK chunk was loading. Re-read both, then once
  // more immediately before the opt-in itself.
  if (!isAnalyticsAllowed()) return
  if (onSitesNoCaptureBoundary()) return
  if (!ph.has_opted_in_capturing()) {
    ph.opt_in_capturing({ captureEventName: false })
  }
}

/** Consent denied or revoked: stop capture immediately. Never initializes. */
export function disablePostHogCapture(): void {
  try {
    client?.opt_out_capturing()
  } catch {
    // Analytics must never break the product.
  }
}

/**
 * Capture a product event. No-ops unless consent was granted and the SDK
 * initialized (missing env vars, pre-consent, SSR are all silent no-ops).
 * Queues behind the in-flight init so events fired right after consent
 * are not lost.
 */
export function capturePostHogEvent(
  name: string,
  props?: EventProperties
): void {
  if (typeof window === 'undefined' || !initPromise) return
  if (onSitesNoCaptureBoundary()) return
  void initPromise.then(ph => {
    if (!ph || !isAnalyticsAllowed()) return
    // An SDK initialized earlier on marketing is still live here, so the
    // boundary is re-read after the awaited init, immediately before capture.
    if (onSitesNoCaptureBoundary()) return
    try {
      ph.capture(name, props ? sanitizeEventProperties(props) : undefined)
    } catch {
      // Analytics must never break the product.
    }
  })
}

/**
 * Manual $pageview for genuine pathname/search navigation only (automatic
 * pageviews are disabled). Deduplicates by URL so consent re-grants and
 * effect re-runs can never double-count a view.
 */
export function capturePostHogPageview(url: string): void {
  if (typeof window === 'undefined' || !initPromise) return
  if (onSitesNoCaptureBoundary()) return
  void initPromise.then(ph => {
    if (!ph || !isAnalyticsAllowed()) return
    // Same last line as capturePostHogEvent: the pathname can have become a
    // Sites one (token in the URL) while this init settled.
    if (onSitesNoCaptureBoundary()) return
    if (url === lastPageviewUrl) return
    lastPageviewUrl = url
    try {
      ph.capture('$pageview')
    } catch {
      // Analytics must never break the product.
    }
  })
}

/** Test-only: reset module state between vitest cases. */
export function _resetPostHogForTests(): void {
  client = null
  initPromise = null
  lastPageviewUrl = null
}
